import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { NamedLink, IconSpinner } from '../../components';
import { useConfiguration } from '../../context/configurationContext';

import css from './LiveBidsCarousel.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import classNames from 'classnames';

const { Money } = sdkTypes;

const SectionHeader = () => (
  <div className={css.header}>
    <h2 className={css.title}>
      <FormattedMessage id="LiveBidsCarousel.title" />
    </h2>
  </div>
);

const CARD_WIDTH = 334;
const CARD_GAP = 20;
const SCROLL_AMOUNT = CARD_WIDTH + CARD_GAP;

const LiveBidsCarousel = ({ listings, inProgress, error, onViewAllBids }) => {
  const intl = useIntl();
  const config = useConfiguration();
  const viewportRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    // Also re-check on resize
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [listings, updateScrollState]);

  const scrollLeft = () => {
    viewportRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const scrollRight = () => {
    viewportRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  if (error) {
    return null;
  }

  if (inProgress) {
    return (
      <div className={css.carouselContainer}>
        <SectionHeader />

        <div className={css.loadingState}>
          <IconSpinner rootClassName={css.spinnerBlack} />
        </div>
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className={css.carouselContainer}>
        <SectionHeader />
        <div className={css.emptyState}>
          <p>
            <FormattedMessage id="LiveBidsCarousel.noActiveBids" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.carouselContainer}>
      <div className={css.headerSection}>
        <div />
        <SectionHeader />

        <div className={classNames(css.viewAllCard, css.desktop)}>
          <button type="button" onClick={onViewAllBids} className={css.viewAllButton}>
            <span className={css.viewAllText}>
              <FormattedMessage id="LiveBidsCarousel.viewAllBids" />
            </span>

            <IconCard brand="rightArrow" />
          </button>
        </div>
      </div>
      <div className={css.carouselNavArea}>
        {/* Prev arrow */}
        {canScrollLeft && (
          <button
            type="button"
            className={css.navArrow + ' ' + css.navArrowLeft}
            onClick={scrollLeft}
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
        )}

        <div className={css.carouselWrapper}>
          <div className={css.carouselViewport} ref={viewportRef}>
            {listings.map((listing, index) => {
              const listingEntity = ensureListing(listing);
              const author = ensureUser(listing?.author);
              const images = listing?.images || [];
              const firstImage = images[0];
              const imageUrl =
                firstImage?.attributes?.variants?.['square-small2x']?.url ||
                firstImage?.attributes?.variants?.['square-small']?.url;

              // Determine if this is a rejected-bid fallback card
              const isRejectedFallback = !!listing.rejectedBid && !listing.highestBid;
              const bidData = isRejectedFallback ? listing.rejectedBid : listing.highestBid;

              const bidAmount = bidData?.amount;
              const bidCurrency = bidData?.currency || config.currency;
              const bidTimestamp = bidData?.timestamp;

              const { title: listingTitle = '', publicData } = listingEntity?.attributes || {};
              const listingSlug = createSlug(listingTitle);
              const listingId = listingEntity?.id?.uuid || listingEntity?.id;

              const authorName =
                author?.attributes?.profile?.displayName ||
                author?.attributes?.profile?.abbreviatedName ||
                '';

              const bidMoney = new Money(bidAmount, bidCurrency);
              const formattedBidAmount = formatMoney(intl, bidMoney);

              const bidDate = bidTimestamp ? new Date(bidTimestamp) : null;
              const formattedTime = bidDate
                ? intl.formatRelativeTime(
                  Math.round((bidDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                  'day'
                )
                : '';

              const isFeatured = Boolean(publicData?.featured);
              const isBootleg =
                publicData?.categoryLevel2 &&
                publicData.categoryLevel2.toLowerCase().includes('bootleg');
              const biddingAvailable =
                publicData?.biddingEnabled && publicData?.listingOpenStatus !== 'pending';

              return (
                <div key={`${listingId}-${index}`} className={css.bidCard}>
                  <NamedLink
                    name="ListingPage"
                    params={{ id: listingId, slug: listingSlug }}
                    to={{ state: { forceScrollTop: true } }}
                    className={css.cardLink}
                  >
                    <div className={css.imageContainer}>
                      {imageUrl ? (
                        <img className={css.image} alt={listingTitle} src={imageUrl} />
                      ) : (
                        <div className={css.imagePlaceholder}>
                          <FormattedMessage id="LiveBidsCarousel.noImage" />
                        </div>
                      )}
                      {isFeatured ? (
                        <span className={css.featureTag}>
                          <FormattedMessage id="listingCard.featureListingTag" />
                        </span>
                      ) : null}
                    </div>

                    <div className={css.cardBody}>
                      <h3 className={css.listingTitle}>{listingTitle}</h3>
                      {authorName ? <p className={css.sellerName}>{authorName}</p> : null}

                      <div className={css.divider} />

                      <div className={css.bidInfoRow}>
                        <div className={css.bidColumnRight}>
                          <span className={css.label}>
                            {isRejectedFallback ? (
                              <FormattedMessage id="LiveBidsCarousel.declinedBid" />
                            ) : (
                              <FormattedMessage id="LiveBidsCarousel.highestBid" />
                            )}
                          </span>
                          <span
                            className={css.amount}
                            style={isRejectedFallback ? { color: '#949494' } : undefined}
                          >
                            {formattedBidAmount}
                          </span>
                        </div>
                      </div>

                      <div className={css.dividerFooter} />

                      <div className={css.footerRow}>
                        {isBootleg ? <span className={css.footerTag}>Bootleg</span> : <span />}
                        {biddingAvailable ? (
                          <span className={css.footerTag}>
                            <FormattedMessage id="listingCard.biddingAvailableSecondary" />
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </NamedLink>
                </div>
              );
            })}

          </div>
        </div>

        {/* Next arrow */}
        {canScrollRight && (
          <button
            type="button"
            className={css.navArrow + ' ' + css.navArrowRight}
            onClick={scrollRight}
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
        )}
      </div>

      <div className={classNames(css.viewAllCard, css.mobile)}>
        <button type="button" onClick={onViewAllBids} className={css.viewAllButton}>
          <span className={css.viewAllText}>
            <FormattedMessage id="LiveBidsCarousel.viewAllBids" />
          </span>

          <IconCard brand="rightArrow" />
        </button>
      </div>
    </div>
  );
};

export default LiveBidsCarousel;
